import { Div, Span, Button } from '@codesuma/baseline'
import calendar, { IDayCell } from '../../../services/calendar'
import settings from '../../../services/settings'
import customOccasions from '../../../services/custom-occasions'
import { dayMarks } from '../../../services/day-holidays'
import phoneCalendar from '../../../services/phone-calendar'
import phoneReminders from '../../../services/phone-reminders'
import dayEvents from '../../../services/day-events'
import ownEvents from '../../../services/events'
import dayColors from '../../../services/day-colors'
import { J_WEEKDAYS_SHORT, G_WEEKDAYS_SHORT, G_MONTHS, G_MONTHS_EN, J_MONTHS, H_MONTHS } from '../../../configs/names'
import { monthColor } from '../../../configs/colors'
import { toFaDigits } from '../../../helpers/digits'
import { solarYear, paleYear } from '../../../helpers/year'
import { MonthList } from './month-list'
import { TaskList, overdueRows, aheadRows } from './task-list'
import { TodayEvents } from './today-events'
import { Upcoming, upcomingRows } from './upcoming'
import { DayCell } from './day-cell'
import { YearLink } from '../../../components/year-link'
import { pullDown } from '../../../helpers/pull-down'
import style from './index.module.css'

// A tall chevron pointing to the start side — drawn for LTR, so it points
// left; the RTL slide mirrors it (see .arrow). The end side's is the same mark
// turned round. Open rather than steep: a narrow V beside a wide grid reads as
// a crease in the page, and the mark has to carry the tap strip it sits in.
const CHEVRON_SVG = '<svg viewBox="0 0 20 48" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5 5 24l10 19"/></svg>'

// One month rendered inside a slider slide: header (month + year), weekday row
// (rotated to the configured
// week start) and a fixed 6×7 day grid — lead-in/lead-out days of the adjacent
// months left empty, each in-month cell optionally showing the
// other solar calendar's day on top and the hijri day below, holidays in red.
// The primary calendar (Jalaali or Gregorian) comes from settings.
export const MonthSlide = (index: number) => {
    const base = Div()
    base.addClass(style.base)

    const { system, showSecondary, showHijri, weekStart, vertical, theme } = settings.get()
    // Gregorian months read left-to-right: grid columns and text alignment
    // both flip with the document direction.
    if (system === 'gregorian') base.attr('dir', 'ltr')
    const month = calendar.monthAt(index, system)

    // In the seasons theme each month owns a color, and it is painted on the
    // slide so it travels with it — two months meet on a visible edge as they
    // slide past. --paper too, since the today circle draws its numbers in it.
    if (theme === 'seasons') {
        const rgb = monthColor(month.month, system)
        const paper = `rgb(${rgb})`
        base.el.style.setProperty('--paper', paper)
        base.style({ backgroundColor: paper })
    }

    // The Persian week always begins on شنبه — the configurable first day is a
    // Gregorian-only preference, kept in settings so switching back restores it.
    const firstDay = system === 'gregorian' ? weekStart : 0

    const cells = calendar.grid(month, system, firstDay)
    const inMonth = cells.filter(c => c.inMonth)

    // Fire and forget: the phone's events for whatever months this grid touches.
    // The fetch is a bridge call away, so the slide is built from what is
    // already held and built again when the answer lands — see the home page's
    // 'phone-events' redraw. Does nothing at all when the switch is off, and on
    // the web, where there is no phone calendar to read. The iPhone's reminders
    // come the same way, for the task list under the grid.
    phoneCalendar.ensure(cells)
    phoneReminders.ensure(cells)

    // The month's span in another system: its distinct months in cell order
    // (usually 2, up to 3 for hijri), names joined with dashes and the year
    // appended per year group — e.g. "ژوئن - ژوئیه 2026", "محرم - صفر ۱۴۴۸",
    // or across a year boundary "دسامبر 2025 - ژانویه 2026".
    const spanLine = (year: (c: IDayCell) => number, monthOf: (c: IDayCell) => number, names: string[], write: (y: number) => string) => {
        const seen: { y: number, m: number }[] = []
        for (const c of inMonth) {
            const [y, m] = [year(c), monthOf(c)]
            if (!seen.some(s => s.y === y && s.m === m)) seen.push({ y, m })
        }
        const parts: string[] = []
        for (let i = 0; i < seen.length;) {
            const y = seen[i].y
            const months: string[] = []
            while (i < seen.length && seen[i].y === y) months.push(names[seen[i++].m - 1])
            parts.push(`${months.join(' - ')} ${write(y)}`)
        }
        return parts.join(' - ')
    }

    // Header
    const header = Div()
    header.addClass(style.header)
    const year = system === 'gregorian' ? String(month.year) : solarYear(month.year)
    const monthName = system === 'gregorian' ? G_MONTHS_EN[month.month - 1] : month.name
    const title = Div()
    title.addClass(style.title)
    title.append(Span(`${monthName} `))
    // The year is the door to the year view — the other way in is to pull the
    // grid past its top (see helpers/pull-down.ts).
    const yearLink = YearLink(paleYear(year), `سال ${year}`)
    yearLink.on('open', () => base.emit('year-open', month.year))
    title.append(yearLink)
    header.append(title)
    // The other calendars, on one line under the month name: the other solar one
    // first, then the hijri — that one only for a reader who asked for hijri
    // days, since it is the same switch that puts them in the day cells. Same
    // shape in either mode, so the header stands the same height whichever
    // calendar is primary.
    const other = system === 'gregorian'
        ? spanLine(c => c.jy, c => c.jm, J_MONTHS, solarYear)
        : spanLine(c => c.gy, c => c.gm, G_MONTHS, String)
    const hijri = spanLine(c => c.hy, c => c.hm, H_MONTHS, toFaDigits)
    const sub = Div()
    sub.html(paleYear(showHijri ? `${other} | ${hijri}` : other))
    sub.addClass(style.sub)
    title.append(sub)

    // Weekday labels (rotated to the configured week start) and the day cells.
    // Adjacent-month days are there, but left empty (see .out).
    const weekdayNames = system === 'gregorian' ? G_WEEKDAYS_SHORT : J_WEEKDAYS_SHORT
    const label = (r: number) => {
        const cell = Div(weekdayNames[(firstDay + r) % 7])
        cell.addClass(style.weekday)
        return cell
    }
    // The weekday comes from the cell's column (its position within the week).
    // The weekend follows the calendar on screen — جمعه alone in Jalaali,
    // Saturday and Sunday in Gregorian — and counts as a holiday alongside the
    // occasion holidays. A tapped cell bubbles up as 'day-open' to route on.
    // In the usual layout each day also carries its own weekday letter, for the
    // day view's line (see .cellLetter). The grid's days are kept, in its order,
    // for the day view; the line makes more for the months either side.
    const comps: ReturnType<typeof DayCell>[] = []
    const dayCell = (c: IDayCell, i: number) => {
        const weekday = (firstDay + (i % 7)) % 7 // 0 = شنبه … 6 = جمعه
        const isWeekend = system === 'gregorian' ? weekday <= 1 : weekday === 6
        // Which side of the calendar each colour belongs to is one rule for the
        // whole app — see services/day-holidays.ts.
        const { primary, other } = dayMarks(c, system, isWeekend)
        // The two wear the same mark. Emrouz's own events only: dayEvents
        // would bring the phone's calendar along with them, and that is what
        // emptied the mark of meaning before.
        const hasCustom = customOccasions.forDay(c).length > 0
        const hasEvent = ownEvents.forDay(c).length > 0
        const comp = DayCell(c, {
            system, showSecondary, showHijri, hasCustom, hasEvent,
            isHoliday: primary, isHolidayOther: other,
            color: dayColors.colorOf(c),
        })
        comp.on('open', () => base.emit('day-open', c))
        if (!c.inMonth) comp.addClass(style.out)
        if (!vertical) {
            const letter = Div(weekdayNames[weekday])
            letter.addClass(style.weekday, style.cellLetter)
            comp.append(letter)
        }
        return comp
    }

    // The whole slide scrolls inside this one band, from the top of the screen
    // down to the home page's bottom bar. The month title rides at the top of it
    // and stays there (see .header), so the days and the occasions pass under
    // the title on their way up and out.
    const scroll = Div()
    scroll.addClass(style.scroll)
    // Named rather than found by class, because the home page has to put a
    // rebuilt month's band back where the last one was left — see the fill
    // handler there.
    scroll.attr('data-band', '')
    base.append(scroll)

    scroll.append(header)
    // The section headers further down stick just under the title, so they need
    // its height — which depends on the text size and on whether the line of
    // other calendars wrapped, so it is measured rather than worked out.
    new ResizeObserver(() =>
        base.el.style.setProperty('--header-h', `${header.el.offsetHeight}px`)).observe(header.el)
    // The title only wants the blur behind it once something is passing under
    // it; at rest there is only the painting there, and that is left sharp.
    scroll.on('scroll', () => header.toggleClass(style.scrolled, scroll.el.scrollTop > 0))

    // Carry on past the top of the band and the year opens — but not from under
    // a day's sheet, where the band is not the reader's to move (see lift).
    pullDown(base, scroll.el, () => !base.el.classList.contains(style.lifted))
    base.on('pull-down', () => base.emit('year-open', month.year))

    // The grid sits in a pane of its own height, so the first section's header
    // comes directly under it rather than down on the bottom bar.
    const pane = Div()
    pane.addClass(style.pane)
    scroll.append(pane)

    // The month steppers, one in each gutter beside the grid, for everyone who
    // never discovers the swipe. In the slide rather than on the page, so they
    // travel with the month they step from. The start side always holds the
    // month before and the end side the month after — the slide's own direction
    // puts them on the right physical sides, the same way it lays out the grid.
    for (const [cls, event] of [[style.arrowPrev, 'month-prev'], [style.arrowNext, 'month-next']]) {
        const arrow = Button()
        arrow.addClass(style.arrow, cls)
        arrow.attr('aria-label', event === 'month-prev' ? 'ماه قبل' : 'ماه بعد')
        arrow.html(CHEVRON_SVG)
        arrow.on('click', () => base.emit(event))
        pane.append(arrow)
    }

    // Declared out here because both layouts below fill the same one. The
    // weekday row too, though only the usual layout has one: the day view
    // measures it (see lift).
    const grid = Div()
    const weekdays = Div()
    // The ripples a press sends out from a day (see below) have a layer of
    // their own, under the days: they spread well past the grid, and the grid
    // cuts off what stands out of it (see .grid).
    const ripples = Div()
    ripples.addClass(style.ripples)
    pane.append(ripples)
    if (vertical) {
        // Transposed: one grid, weekday labels down the right column, weeks as
        // columns. The 42 cells are already week-major, so appending labels then
        // cells in order lines up with grid-auto-flow: column (see .vgrid).
        grid.addClass(style.vgrid)
        for (let r = 0; r < 7; r++) grid.append(label(r))
        cells.forEach((c, i) => grid.append(comps[i] = dayCell(c, i)))
        pane.append(grid)
    } else {
        weekdays.addClass(style.weekdays)
        for (let r = 0; r < 7; r++) weekdays.append(label(r))
        pane.append(weekdays)

        grid.addClass(style.grid)
        cells.forEach((c, i) => grid.append(comps[i] = dayCell(c, i)))
        pane.append(grid)
    }

    // A press anywhere on the grid is a press on a day. Between two rows, or in
    // the hollow where four round days meet, it goes to the nearest one, so the
    // reader never has to find the number itself. Unless that one is out of
    // sight, and then it is on nothing: the grid leaves the other months' days
    // empty (see .out), and under a day's sheet all its rows but one are cut
    // off or covered (see lift). The line is all in sight.
    const dayAt = (e: MouseEvent) => {
        const days = line ? line.comps : comps
        const hit = days.find(d => d.el.contains(e.target as Node))
        if (hit || e.target !== grid.el) return hit
        let near = days[0]
        let best = Infinity
        for (const d of days) {
            const r = d.el.getBoundingClientRect()
            const far = Math.hypot(r.left + r.width / 2 - e.clientX, r.top + r.height / 2 - e.clientY)
            if (far < best) [near, best] = [d, far]
        }
        const r = near.el.getBoundingClientRect()
        return near.el.contains(document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2)) ? near : undefined
    }

    // The pressed day dips (see DayCell), and a disc of ink spreads out from
    // it, well past its column, so that even under a broad finger the reader
    // sees which day took the press. From the day's disc rather than from the
    // finger, which between the days is not on it. It holds while pressed and
    // fades once let go. Moving the pointer lets go, since that is a scroll or
    // a swipe; pointerleave also fires when the month slider takes the pointer
    // for a horizontal swipe.
    let pressed: ReturnType<typeof DayCell> | undefined
    let wave: ReturnType<typeof Div> | undefined
    let startX = 0
    let startY = 0
    const ripple = (day: ReturnType<typeof DayCell>) => {
        const box = ripples.el.getBoundingClientRect()
        const r = day.el.getBoundingClientRect()
        const disc = getComputedStyle(day.el)
        wave = Div()
        wave.addClass(style.ripple)
        wave.style({
            left: `${r.left + r.width / 2 - box.left}px`,
            top: `${r.top + r.height * parseFloat(disc.getPropertyValue('--disc-y')) / 100 - box.top}px`,
            width: `${r.width * parseFloat(disc.getPropertyValue('--disc-w')) / 100}px`,
        })
        ripples.append(wave)
        wave.el.animate([
            { transform: 'translate(-50%, -50%) scale(0.6)', opacity: 0.3 },
            { transform: 'translate(-50%, -50%) scale(3.2)', opacity: 0.12 },
        ], { duration: 500, easing: 'cubic-bezier(0.2, 0, 0, 1)', fill: 'forwards' })
    }
    const release = () => {
        pressed?.press(false)
        const gone = wave
        wave = undefined
        if (gone) gone.el.animate({ opacity: 0 }, { duration: 300, fill: 'forwards' }).onfinish = () => gone.remove()
    }
    grid.el.addEventListener('pointerdown', (e: PointerEvent) => {
        release()
        startX = e.clientX
        startY = e.clientY
        pressed = dayAt(e)
        if (!pressed) return
        ripple(pressed)
        pressed.press(true)
    })
    grid.el.addEventListener('pointermove', (e: PointerEvent) => {
        if (Math.abs(e.clientX - startX) > 8 || Math.abs(e.clientY - startY) > 8) release()
    })
    for (const type of ['pointerup', 'pointercancel', 'pointerleave']) grid.el.addEventListener(type, release)
    // A tap between the days is a click on the grid itself: hand it on to the
    // day the press went to. Not worked out again from the click, whose point
    // the browser moves toward whatever it thinks the finger was after.
    grid.el.addEventListener('click', (e: MouseEvent) => {
        if (e.target === grid.el) pressed?.el.click()
    })

    // The band under the grid, in the order it is read: what you are behind on
    // comes first, then what is still ahead of you this month, then today's own
    // events, then your occasions and events coming up, and last everything the
    // month itself holds. The first four are the user's own days and only
    // belong on the slide whose month holds today —
    // inMonth as well as isToday, since today also shows up as a lead-out cell
    // on the month before, and that month is not where it belongs.
    const today = cells.find(c => c.isToday && c.inMonth)
    type ISection = ReturnType<typeof MonthList>
    const sections: ISection[] = []
    // A section whose rows each lead to a day of their own: pass the tap on to
    // the page, the same trip a day cell makes.
    const routed = (section: ISection) => {
        section.on('day-open', (c: IDayCell) => base.emit('day-open', c))
        return section
    }
    if (today) {
        // Nothing to be behind on is worth no header at all, so that list only
        // appears when it has rows.
        const overdue = overdueRows()
        if (overdue.length) sections.push(routed(TaskList('کارهای عقب‌مانده', system, overdue, scroll.el, { withMonth: true })))
        // The month's own list is always here, empty or not: its last line is
        // the way to make a task, and a month with nothing planned is exactly
        // when that is wanted.
        const ahead = TaskList('کارهای پیش رو', system, aheadRows(inMonth), scroll.el, { canAdd: true })
        ahead.on('add-task', () => base.emit('add-task'))
        sections.push(routed(ahead))
        if (dayEvents.forDay(today).length) sections.push(routed(TodayEvents(system, today, scroll.el)))
        const upcoming = upcomingRows(inMonth)
        if (upcoming.length) sections.push(routed(Upcoming(system, upcoming, scroll.el)))
    }
    const monthList = MonthList(system, cells, scroll.el)
    monthList.on('add', () => base.emit('add-to-month'))
    sections.push(monthList)
    // Only the first section meets the grid, so only it needs the gap.
    if (vertical) sections[0].addClass(style.verticalGap)
    for (const section of sections) scroll.append(section)

    // The day view: a day's sheet up over the calendar (see pages/home and
    // pages/day). The whole band rises as one piece until the weekday letters
    // stand on the menu icon's line, the title and the line of other calendars
    // fading as they go up off the screen. The days go on up inside the grid
    // until the day's row — its week, or its weekday in the transposed grid —
    // is the first: the rows before it pass up under the letters and out of
    // sight. So one row of days is left, the day ringed in it, and the sheet
    // comes up from the bottom over the rest and stops just under that row.
    // Returns where that is, down from the top of the screen, for the sheet to
    // stop at. With null it all settles back, and there is nowhere to stop.
    //
    // In the usual grid the row then becomes the line: once the band is up,
    // every day of the grid stands in that one row, with the months either side
    // on its two ends, and it scrolls sideways a day at a time (see .lined). It
    // starts on the day's week, in the row's own place, so nothing seems to
    // change. From then on the ring follows the sheet's day along it, and when
    // the day leaves the seven in view, the line slides to the day's week. The
    // transposed grid keeps its row: that is one weekday across the month, not
    // a week, so there is nothing to run on into.
    //
    // `instant` is for a month built while the sheet is already up: it starts
    // out risen, its line laid out at once — from `from`, the first day in view
    // on the line of the month it replaces, or else from the day's week.
    let picked: ReturnType<typeof DayCell> | undefined
    let stop: number | undefined
    let line: { days: IDayCell[], comps: ReturnType<typeof DayCell>[], extra: ReturnType<typeof DayCell>[] } | null = null
    let lining = 0 // the timer that lays the line out once the band is up
    let gliding = 0 // the frame of a slide along the line
    let folding = 0 // the timer for the grid coming back down (see fold)

    const sameDay = (a: IDayCell, b: IDayCell) => a.gy === b.gy && a.gm === b.gm && a.gd === b.gd
    const ring = (comp?: ReturnType<typeof DayCell>) => {
        picked?.removeClass(style.picked)
        picked = comp
        picked?.addClass(style.picked)
    }
    const rem = () => parseFloat(getComputedStyle(document.documentElement).fontSize)
    // Where a day's middle is, to the fraction of a pixel. offsetTop drops the
    // fraction, and a row moved up by the rounded figure stands up to a pixel
    // off the place the line takes over in, which shows as a jump. The middle,
    // because a day just pressed is still dipping (see DayCell), and a dip
    // leaves only its middle where it was.
    const mid = (el: HTMLElement) => {
        const r = el.getBoundingClientRect()
        return { x: r.left + r.width / 2, y: r.top + r.height / 2 }
    }
    // A day's width along the line, and how many days along it the view
    // starts. A right-to-left line scrolls into negative numbers. The width
    // comes off the pane, which is exactly the seven days: a cell's own width
    // is rounded to a whole pixel, and that adds up along a line of a hundred.
    const sign = system === 'gregorian' ? 1 : -1
    const col = () => pane.el.getBoundingClientRect().width / 7
    const at = () => Math.abs(grid.el.scrollLeft) / col()

    // Slides the line until day k is the first in view, in the sheet's own
    // curve. Snapping is off meanwhile, or each frame would be pulled to a day.
    const GLIDE_MS = 220
    const glide = (k: number) => {
        cancelAnimationFrame(gliding)
        const from = grid.el.scrollLeft
        const to = sign * k * col()
        if (Math.abs(to - from) < 1) return
        const start = performance.now()
        grid.el.style.scrollSnapType = 'none'
        const step = (now: number) => {
            const p = Math.min(1, Math.max(0, (now - start) / GLIDE_MS))
            grid.el.scrollLeft = from + (to - from) * (1 - (1 - p) ** 3)
            if (p < 1) gliding = requestAnimationFrame(step)
            else grid.el.style.scrollSnapType = ''
        }
        gliding = requestAnimationFrame(step)
    }

    // The day at i along the line, in view: when it is not, the line slides to
    // its week.
    const show = (i: number) => {
        const k = Math.round(at())
        if (i < k || i >= k + 7) glide(Math.min(i - i % 7, line!.days.length - 7))
    }

    // The months either side as far as their own grids reach: the one before
    // from the week of its first day, the one after to the end of its sixth
    // week. Whole weeks both, so every day keeps to its column.
    const lay = () => {
        const time = (c: IDayCell) => Date.UTC(c.gy, c.gm - 1, c.gd)
        const near = (step: number) => calendar.grid(calendar.monthAt(index + step, system), system, firstDay)
            .map(c => ({ ...c, inMonth: true }))
        const before = near(-1).filter(c => time(c) < time(cells[0]))
        const after = near(1).filter(c => time(c) > time(cells[41]))
        const head = before.map(dayCell)
        const tail = after.map(dayCell)
        // Measured while the grid still has its own shape.
        base.el.style.setProperty('--grid-h', `${grid.el.offsetHeight}px`)
        base.el.style.setProperty('--letters-h', `${grid.el.offsetTop - weekdays.el.offsetTop}px`)
        grid.el.prepend(...head.map(c => c.el))
        grid.el.append(...tail.map(c => c.el))
        line = { days: [...before, ...cells, ...after], comps: [...head, ...comps, ...tail], extra: [...head, ...tail] }
        base.addClass(style.lined)
    }

    const unlay = () => {
        cancelAnimationFrame(gliding)
        line?.extra.forEach(c => c.remove())
        line = null
        grid.style({ translate: '' })
        base.removeClass(style.lined)
    }

    // Back down. Off the line first, straight back to the week of the sheet's
    // day, wherever the reader had scrolled to: the grid takes its own shape
    // again with that week risen into the first row's place and cut off above
    // and below it. The sheet's day is always in this month — the page builds
    // the month the day goes to — so the week is one of the grid's rows. From
    // there it all comes down together, the rows before it out from under the
    // letters and the rows after it out from under the row.
    const fold = () => {
        if (line) {
            const g = comps.indexOf(picked!)
            const row = comps[g - g % 7]
            base.addClass(style.still)
            unlay()
            const top = mid(row.el).y - mid(comps[0].el).y
            base.el.style.setProperty('--shift', `${-top}px`)
            base.el.style.setProperty('--cut', `${top - 0.4 * rem()}px`)
            base.el.style.setProperty('--cut-bottom', `${grid.el.offsetHeight - top - row.el.offsetHeight - 0.6 * rem()}px`)
            void base.el.offsetHeight
            base.removeClass(style.still)
        }
        ring(undefined)
        base.removeClass(style.lifted)
        folding = window.setTimeout(() => { folding = 0 }, 280) // see .scroll and .grid
    }

    // A press while the grid is still coming down brings it to rest at once.
    // The rows after the day unroll from under it last, so a press on where
    // one of them is going would otherwise land on nothing.
    base.el.addEventListener('pointerdown', () => {
        if (!folding) return
        clearTimeout(folding)
        folding = 0
        base.addClass(style.still)
        void base.el.offsetHeight
        base.removeClass(style.still)
    }, true)

    const lift = (c: IDayCell | null, instant = false, from?: IDayCell) => {
        clearTimeout(lining)
        clearTimeout(folding)
        folding = 0
        if (!c) {
            fold()
            return
        }
        if (line) {
            const i = line.days.findIndex(d => sameDay(d, c))
            ring(line.comps[i])
            if (i >= 0) show(i)
            return stop
        }
        ring(comps[cells.findIndex(x => sameDay(x, c))])
        // How far the band goes. The letters start where the header ends,
        // and their middle goes to the menu icon's, 30px under the status
        // bar. The transposed grid has its letters in a column down the
        // icon's side instead, so there the grid's top stops just under the
        // icon. The header's top padding is the status bar and 3.4rem (see
        // .header).
        const safeTop = parseFloat(getComputedStyle(header.el).paddingTop) - 3.4 * rem()
        const rise = vertical
            ? header.el.offsetHeight - (safeTop + 60)
            : header.el.offsetHeight + weekdays.el.offsetHeight / 2 - (safeTop + 30)
        // And how far the days go on inside the grid: the day's row from the
        // first one. The grid is cut off just above the day's row: high enough
        // for what of the row stands over its cells, the ring or a hijri day,
        // and low enough to leave out the discs of the row before, which reach
        // down into the row gap (see .lifted .grid).
        const shift = picked ? mid(picked.el).y - mid(comps[0].el).y : 0
        base.el.style.setProperty('--rise', `${-rise}px`)
        base.el.style.setProperty('--shift', `${-shift}px`)
        base.el.style.setProperty('--cut', `${shift - 0.4 * rem()}px`)
        base.el.style.removeProperty('--cut-bottom')
        // The day's row ends up in the first row's place, so it ends where the
        // first row does once everything is up: where that is now, less however
        // far the band and the grid have gone up so far, plus however far down
        // the band is scrolled — the home page takes it back to its top — less
        // the band's whole rise. The sheet stops 0.6rem under that: clear of
        // the ring, which reaches below the cell, and above the next row, which
        // starts a 0.85rem row gap down (see .grid) and reaches a little higher
        // than that only where today's disc is.
        const gone = new DOMMatrix(getComputedStyle(scroll.el).transform).m42
            + new DOMMatrix(getComputedStyle(grid.el).transform).m42
        stop = comps[0].el.getBoundingClientRect().bottom - gone + scroll.el.scrollTop - rise + 0.6 * rem()
        if (instant) base.addClass(style.still)
        base.addClass(style.lifted)
        if (instant) {
            void base.el.offsetHeight
            base.removeClass(style.still)
        }
        if (!vertical) {
            const enter = () => {
                // A month rebuilt meanwhile has left this one out of the page.
                if (!base.el.isConnected) return
                lay()
                const find = (d: IDayCell) => line!.days.findIndex(x => sameDay(x, d))
                const i = find(c)
                const k = from && find(from) >= 0 ? find(from) : i - i % 7
                grid.el.scrollLeft = sign * k * col()
                // A scroll position is kept to whole pixels, and a day is not
                // a whole number of them wide, so the week can stop up to half
                // a pixel off the columns the row stood in. The line is moved
                // back by that much, so it takes over from the row unseen.
                const p = pane.el.getBoundingClientRect()
                const first = sign < 0 ? p.right - col() / 2 : p.left + col() / 2
                grid.style({ translate: `${first - mid(line!.comps[k].el).x}px` })
                ring(line!.comps[i])
                show(i)
            }
            if (instant) enter()
            else lining = window.setTimeout(enter, 300)
        }
        return stop
    }

    // The first day in view on the line, for a month rebuilt under the sheet
    // to lay out its own line from.
    const first = () => line?.days[Math.round(at())]

    return Object.assign(base, { lift, first })
}
